import { createFileRoute } from "@tanstack/react-router";
import { ImageCardGrid, type ImageCardItem } from "@/components/ImageCardGrid";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";

import { fetchRecipes, type RecipeRow } from "@/lib/queries";
import { PaywallBadge } from "@/components/PaywallBadge";

import { SeoNote } from "@/components/SeoNote";
import { PageTuneSlot } from "@/components/TunePanel";
import { usePaywallFilter } from "@/hooks/use-paywall-filter";

const PAGE_SIZE = 30;

const CATEGORY_LABEL: Record<string, string> = {
  groent: "Grønt",
  koed_fisk: "Kød & fisk",
  bagvaerk_broed: "Bagværk & brød",
  soede_sager: "Søde sager",
  drikke: "Drikke",
  basis: "Basis",
};

const TRADITION_LABEL: Record<string, string> = {
  dansk: "Dansk",
  nordisk: "Nordisk",
  italiensk: "Italiensk",
  fransk: "Fransk",
  spansk: "Spansk",
  mellemoestlig: "Mellemøstlig",
  nordafrikansk: "Nordafrikansk",
  japansk: "Japansk",
  kinesisk: "Kinesisk",
  sydoestasiatisk: "Sydøstasiatisk",
  indisk: "Indisk",
  mexicansk: "Mexicansk",
  amerikansk: "Amerikansk",
  joedisk: "Jødisk",
  andet: "Andet",
};

type CategoryTab = "alle" | keyof typeof CATEGORY_LABEL;
type TraditionTab = "alle" | keyof typeof TRADITION_LABEL;

const CATEGORY_TABS: { key: CategoryTab; label: string }[] = [
  { key: "alle", label: "Alle" },
  { key: "groent", label: "Grønt" },
  { key: "koed_fisk", label: "Kød & fisk" },
  { key: "bagvaerk_broed", label: "Bagværk & brød" },
  { key: "soede_sager", label: "Søde sager" },
  { key: "drikke", label: "Drikke" },
  { key: "basis", label: "Basis" },
];

const TRADITION_TABS: { key: TraditionTab; label: string }[] = [
  { key: "alle", label: "Alle kilder" },
  { key: "dansk", label: "Dansk" },
  { key: "nordisk", label: "Nordisk" },
  { key: "italiensk", label: "Italiensk" },
  { key: "fransk", label: "Fransk" },
  { key: "spansk", label: "Spansk" },
  { key: "mellemoestlig", label: "Mellemøstlig" },
  { key: "nordafrikansk", label: "Nordafrikansk" },
  { key: "japansk", label: "Japansk" },
  { key: "kinesisk", label: "Kinesisk" },
  { key: "sydoestasiatisk", label: "Sydøstasiatisk" },
  { key: "indisk", label: "Indisk" },
  { key: "mexicansk", label: "Mexicansk" },
  { key: "amerikansk", label: "Amerikansk" },
  { key: "joedisk", label: "Jødisk" },
  { key: "andet", label: "Andet" },
];

export const Route = createFileRoute("/opskrifter")({
  loader: async ({ context }) => {
    const recipes = await context.queryClient.ensureQueryData({
      queryKey: ["recipes"],
      queryFn: fetchRecipes,
    });
    return { recipes };
  },
  head: () => ({
    meta: [
      { title: "Opskrifter – Flâneriet" },
      { name: "description", content: "Én kurateret opskrift om dagen – madnørdet håndværk fra hele verden, dansk hverdag, italiensk trattoria, japansk hjemmemad, mellemøstlig levantinsk og alt derimellem. Sæson og råvare først. Vi linker altid til kilden." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Opskrifter – Flâneriet" },
      { property: "og:description", content: "Én kurateret opskrift om dagen – madnørdet håndværk fra hele verden, altid i sæson." },
      { property: "og:url", content: "https://flaneriet.online/opskrifter" },
      { property: "og:image", content: "https://flaneriet.online/og/og-opskrifter.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Flâneriet – Opskrifter" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Opskrifter – Flâneriet" },
      { name: "twitter:description", content: "Én kurateret opskrift om dagen – madnørdet håndværk fra hele verden, altid i sæson." },
      { name: "twitter:image", content: "https://flaneriet.online/og/og-opskrifter.png" },
    ],
    links: [{ rel: "canonical", href: "https://flaneriet.online/opskrifter" }],
  }),
  component: OpskrifterPage,
});

function OpskrifterPage() {
  const [category, setCategory] = useState<CategoryTab>("alle");
  const [tradition, setTradition] = useState<TraditionTab>("alle");
  const { hidePaywall } = usePaywallFilter();
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const recipes = useQuery({ queryKey: ["recipes"], queryFn: fetchRecipes });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [category, tradition, query, hidePaywall]);

  const q = query.trim().toLowerCase();

  const filtered = useMemo<RecipeRow[]>(() => {
    let list: RecipeRow[] = recipes.data ?? [];
    if (category !== "alle") list = list.filter((r) => r.category === category);
    if (tradition !== "alle") list = list.filter((r) => r.tradition === tradition);
    if (hidePaywall) list = list.filter((r) => !r.paywall);
    if (!q) return list;
    return list.filter((r) => {
      const hay = [r.title, r.chef, r.source, r.summary, r.ingredients, r.category, r.tradition]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [recipes.data, category, tradition, hidePaywall, q]);

  return (
    <div>
      <PageTuneSlot>
        <section>
          <p className="eyebrow mb-3">Søg</p>
          <div className="relative">
            <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
            <label className="sr-only" htmlFor="tune-recipe-search">Søg i opskrifter</label>
            <input
              id="tune-recipe-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søg efter råvare, kok eller ret…"
              className="w-full bg-transparent border-0 border-b border-rule outline-none focus:ring-0 focus:border-bordeaux py-2 pl-7 pr-2 font-sans text-[14px] text-ink placeholder:text-ink-soft transition-colors"
            />
          </div>
        </section>

        <section>
          <p className="eyebrow mb-3">Kategori</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_TABS.map((t) => {
              const active = category === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setCategory(t.key)}
                  aria-pressed={active}
                  className={`eyebrow px-3 py-1.5 rounded-full border transition-colors ${
                    active
                      ? "border-bordeaux text-bordeaux bg-bordeaux/5"
                      : "border-rule text-ink-soft hover:text-bordeaux hover:border-bordeaux"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <p className="eyebrow mb-3">Tradition</p>
          <div className="flex flex-wrap gap-2">
            {TRADITION_TABS.map((t) => {
              const active = tradition === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTradition(t.key)}
                  aria-pressed={active}
                  className={`eyebrow px-3 py-1.5 rounded-full border transition-colors ${
                    active
                      ? "border-bordeaux text-bordeaux bg-bordeaux/5"
                      : "border-rule text-ink-soft hover:text-bordeaux hover:border-bordeaux"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>
      </PageTuneSlot>

      <header className="pt-12 pb-6 border-b-[3px] border-b-double border-b-ink">
        <p className="eyebrow mb-2.5">Årstidens køkken · udvalgte</p>
        <h1 className="font-serif font-normal text-[2.4rem] md:text-[3rem] leading-[1.02] text-ink m-0">
          {category === "alle" ? "Opskrifter" : CATEGORY_LABEL[category]}
          {tradition !== "alle" ? <span className="text-ink-soft"> · {TRADITION_LABEL[tradition]}</span> : null}
        </h1>
        <p className="meta italic mt-3 max-w-xl">
          Én ny opskrift om dagen – sæson og råvare før teknik og prestige. Vi linker altid til kokken.
        </p>
      </header>





      <p className="meta italic pb-2">
        {q ? (
          <>Søgning efter «{query}» · {filtered.length} {filtered.length === 1 ? "opskrift" : "opskrifter"}</>
        ) : (
          <>{filtered.length} {filtered.length === 1 ? "opskrift" : "opskrifter"}, nyeste først.</>
        )}
      </p>

      <section className="min-w-0 pt-6">
        {filtered.length > 0 ? (
          <ImageCardGrid
            saveKind="recipe"
            items={filtered.slice(0, visible).map((r): ImageCardItem => ({
              id: r.id,
              kicker: (
                <span className="inline-flex items-center gap-2">
                  {r.category ? CATEGORY_LABEL[r.category] ?? "Opskrift" : "Opskrift"}
                  {r.tradition && r.tradition !== "andet" ? ` · ${TRADITION_LABEL[r.tradition]}` : ""}
                  <PaywallBadge paywall={r.paywall} />
                </span>
              ),
              title: r.title,
              description: r.summary || undefined,
              meta: (
                <>
                  {r.chef ?? r.source}
                  {r.chef && r.source && r.chef !== r.source ? ` · ${r.source}` : ""}
                </>
              ),
              imageUrl: r.image_url,
              category: r.category ?? "mad",
              ratio: "4/3",
              href: r.original_url ?? undefined,
            }))}


          />
        ) : (
          <p className="meta italic py-16 text-center">Endnu ingen opskrifter her. Kom tilbage om lidt.</p>
        )}
      </section>



      {filtered.length > visible && (
        <div className="pb-6 text-center">
          <button
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="eyebrow text-bordeaux border-b border-bordeaux pb-0.5 hover:opacity-70 transition-opacity"
          >
            Vis flere ({filtered.length - visible} tilbage)
          </button>
        </div>
      )}

      <SeoNote eyebrow="Om samlingen">
        <p>
          Opskrifter er Flâneriets køkkenhjørne – én kurateret ret om dagen, valgt for håndværk,
          stemme og sæson. Vi lader os inspirere af Brdr. Price, Søren Ryge, Camilla Plum, Per Kølster,
          Eva Ravnbøl, Marcella Hazan og Rachel Roddy – men også af Anthony Bourdain, David Chang,
          Claudia Roden, Fuchsia Dunlop, Yasmin Khan, Andrea Nguyen, Edna Lewis og Enrique Olvera.
          Mad som kultur, ikke som performance. Ingen hacks, ingen rangeringer. Alt fører videre
          til den oprindelige kilde.
        </p>
      </SeoNote>

    </div>
  );
}

